import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bot, User, ExternalLink } from "lucide-react";
import { ChatMessage as ChatMessageType } from "@/lib/chatService";
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { detectToolsAndResources } from "@/lib/toolsResourcesMap";
import { useNavigate } from 'react-router-dom';
import { useCurrentLanguage } from '@/components/LanguageRedirect';

interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage = ({ message }: ChatMessageProps) => {
  const isUser = message.role === 'user';
  const navigate = useNavigate();
  const currentLang = useCurrentLanguage();
  
  // Detectar herramientas y recursos mencionados (solo para mensajes del asistente)
  const detectedItems = !isUser ? detectToolsAndResources(message.content) : [];
  
  return (
    <div className={cn(
      "flex gap-3 max-w-4xl mx-auto",
      isUser ? "justify-end" : "justify-start"
    )}>
      {!isUser && (
        <Avatar className="w-8 h-8 mt-1 flex-shrink-0">
          <AvatarFallback className="bg-blue-100 text-blue-600">
            <Bot className="w-4 h-4" />
          </AvatarFallback>
        </Avatar>
      )}
      
      <Card className={cn(
        "max-w-[80%] lg:max-w-[70%]",
        isUser 
          ? "bg-blue-600 text-white border-blue-600" 
          : "bg-white border-gray-200"
      )}>
        <CardContent className="p-3">
          <div className={cn(
            "text-sm leading-relaxed prose prose-sm max-w-none",
            isUser ? "text-white prose-invert" : "text-gray-900",
            // Estilos personalizados para Markdown
            "prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2",
            "prose-p:my-2",
            "prose-ul:my-2 prose-ol:my-2",
            "prose-li:my-1",
            "prose-strong:font-bold",
            "prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded",
            "prose-a:text-blue-600 prose-a:underline prose-a:font-medium hover:prose-a:text-blue-800",
            isUser && "prose-code:bg-blue-700 prose-a:text-blue-200 hover:prose-a:text-blue-100"
          )}>
            {isUser ? (
              <div className="whitespace-pre-wrap">{message.content}</div>
            ) : (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, href, children, ...props }) => {
                    // Check if it's an internal link (starts with /)
                    const isInternal = href?.startsWith('/');
                    
                    if (isInternal && href) {
                      // Check if the URL already contains a language prefix
                      const hasLangPrefix = href.match(/^\/[a-z]{2}(\/|$)/);
                      
                      // If it doesn't have a language prefix, add the current language
                      const finalHref = hasLangPrefix ? href : `/${currentLang}${href}`;
                      
                      return (
                        <a
                          href={finalHref}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(finalHref);
                          }}
                          className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    }
                    
                    // External links
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
          
          {/* Botones de herramientas y recursos detectados */}
          {!isUser && detectedItems.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2 font-medium">
                📌 Recursos relacionados:
              </p>
              <div className="flex flex-wrap gap-2">
                {detectedItems.map((item) => {
                  // Check if the URL already contains a language prefix
                  const hasLangPrefix = item.url.match(/^\/[a-z]{2}(\/|$)/);
                  
                  // If it doesn't have a language prefix, add the current language
                  const finalUrl = hasLangPrefix ? item.url : `/${currentLang}${item.url}`;
                  
                  return (
                    <Button
                      key={item.id}
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(finalUrl)}
                      className="text-xs h-auto py-1.5 px-3 bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:text-blue-600 hover:border-blue-400 transition-colors cursor-pointer"
                    >
                      <span className="mr-1.5">{item.icon}</span>
                      {item.name}
                      <ExternalLink className="ml-1.5 h-3 w-3" />
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className={cn(
            "text-xs mt-2 opacity-70",
            isUser ? "text-blue-100" : "text-gray-500"
          )}>
            {message.timestamp.toLocaleTimeString('es-ES', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </CardContent>
      </Card>
      
      {isUser && (
        <Avatar className="w-8 h-8 mt-1 flex-shrink-0">
          <AvatarFallback className="bg-gray-100 text-gray-600">
            <User className="w-4 h-4" />
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};